import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { Upload, CheckCircle2, Loader2, Activity, Download, UserPlus, UserCheck, ShieldCheck, Users } from 'lucide-react';
import Papa, { ParseResult } from 'papaparse';
import { Button } from '../../components/ui/Button';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { normalizeOrg } from '../../lib/utils';

type WizardStep = 'upload' | 'mapping' | 'reconciliation' | 'importing' | 'complete';
interface CSVRow { [key: string]: string; }
interface ReconciledRow {
  csvRow: CSVRow;
  matchType: 'new' | 'exact';
  existingId: string | null;
  matchedOrgId: string | null;
  manualOrgId?: string | null;
  isAlreadyEnrolled?: boolean;
}

const INITIAL_MAPPING = { first_name: '', last_name: '', email: '', phone: '', organization: '' };

export const RosterImportWizard: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const engagementId = searchParams.get('engagementId');

  const [step, setStep] = useState<WizardStep>('upload');
  const [parsedData, setParsedData] = useState<CSVRow[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>(INITIAL_MAPPING);
  const [reconciliation, setReconciliation] = useState<{ new: number; matched: number; rows: ReconciledRow[] }>({ new: 0, matched: 0, rows: [] });
  const [importResults, setImportResults] = useState({ success: 0, updated: 0, enrolled: 0, errors: 0 });
  const [isProcessing, setIsProcessing] = useState(false);

  const { data: allOrgs } = useQuery({
    queryKey: ['all-orgs-roster-lookup'],
    queryFn: async () => (await supabase.from('organizations').select('id, name').order('name')).data?.map(o => ({ id: o.id, name: o.name })) || []
  });

  const downloadTemplate = () => {
    const csvContent = "First Name,Last Name,Email,Phone,Organization\nJane,Doe,jane@example.com,555-0199,Example Org";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'engagement_roster_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      Papa.parse(e.target.files[0], {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (results: ParseResult<CSVRow>) => {
          const headers = results.meta.fields || (results.data[0] ? Object.keys(results.data[0]) : []);
          setMapping(prev => {
             const next = { ...prev };
             headers.forEach(h => {
               const l = h.toLowerCase().trim();
               if(l.includes('first')) next.first_name = h;
               if(l.includes('last')) next.last_name = h;
               if(l.includes('email')) next.email = h;
               if(l.includes('phone')) next.phone = h;
               if(l.includes('org')) next.organization = h;
             });
             return next;
          });
          setParsedData(results.data);
          setStep('mapping');
        }
      });
    }
  };

  const startReconciliation = async () => {
    setIsProcessing(true);
    const emails = Array.from(new Set(parsedData.map(r => r[mapping.email]?.toLowerCase().trim()).filter(Boolean)));
    const orgNames = Array.from(new Set(parsedData.map(r => r[mapping.organization]?.trim()).filter(Boolean)));
    
    const [peopleRes, orgsRes, rosterRes] = await Promise.all([
      supabase.from('people').select('id, email').in('email', emails),
      supabase.from('organizations').select('id, name').in('name', orgNames),
      supabase.from('participation').select('person_id').eq('engagement_id', engagementId!)
    ]);

    const peopleMap = new Map<string, string>(peopleRes.data?.map(p => [p.email?.toLowerCase().trim() || '', p.id]) || []);
    const orgMap = new Map<string, string>(orgsRes.data?.map(o => [normalizeOrg(o.name), o.id]) || []);
    const rosterSet = new Set(rosterRes.data?.map(r => r.person_id) || []);

    const rows: ReconciledRow[] = parsedData.map(row => {
      const email = row[mapping.email]?.toLowerCase().trim();
      const orgName = row[mapping.organization]?.trim();
      const existingId = email ? peopleMap.get(email) : null;
      return { 
        csvRow: row, 
        matchType: existingId ? 'exact' : 'new', 
        existingId: (existingId as string) || null,
        matchedOrgId: orgName ? ((orgMap.get(normalizeOrg(orgName)) as string) || null) : null,
        isAlreadyEnrolled: existingId ? rosterSet.has(existingId) : false
      };
    });

    setReconciliation({ new: rows.filter(r => r.matchType === 'new').length, matched: rows.filter(r => r.matchType === 'exact').length, rows });
    setIsProcessing(false);
    setStep('reconciliation');
  };

  const runImport = async () => {
    setIsProcessing(true);
    setStep('importing');
    let success = 0, updated = 0, enrolled = 0, errors = 0;

    for (const item of reconciliation.rows) {
      try {
        const payload = {
          first_name: item.csvRow[mapping.first_name]?.trim() || 'Attendee',
          last_name: item.csvRow[mapping.last_name]?.trim() || 'New',
          email: item.csvRow[mapping.email]?.toLowerCase().trim() || null,
          phone: item.csvRow[mapping.phone]?.replace(/\D/g, '') || null,
          organization_id: item.manualOrgId || item.matchedOrgId || null,
          created_by_email: profile?.email
        };

        let personId = item.existingId;
        if (item.matchType === 'exact' && item.existingId) {
          await supabase.from('people').update(payload).eq('id', item.existingId);
          updated++;
        } else {
          // Identity Creation with defensive read-back for RLS environments
          const { data, error: iErr } = await supabase.from('people').insert([payload]).select('id').single();
          if (iErr) throw iErr;
          
          personId = data?.id;
          
          // Fallback if RLS blocks read-back: find by email
          if (!personId && payload.email) {
            const { data: fallback } = await supabase.from('people').select('id').eq('email', payload.email).single();
            personId = fallback?.id;
          }
          
          if (!personId) throw new Error('Persistence verification failed');
          success++;
        }

        if (engagementId && personId) {
          await supabase.from('participation').upsert({
            engagement_id: engagementId,
            person_id: personId,
            status: 'active',
            created_by_email: profile?.email
          }, { onConflict: 'engagement_id,person_id' });
          enrolled++;
        }
      } catch (err) { 
        console.error("Roster Import Failure:", err);
        errors++; 
      }
    }

    setImportResults({ success, updated, enrolled, errors });
    setStep('complete');
    setIsProcessing(false);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-24">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase">Strategic Roster Sync</h1>
          <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Personnel Enrollment Workflow</p>
        </div>
        <button onClick={() => navigate(-1)} className="text-[10px] font-black uppercase text-slate-400 hover:text-slate-900 tracking-widest">ABORT</button>
      </div>

      <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden min-h-[500px] flex flex-col">
        {step === 'upload' && (
          <div className="flex-1 p-20 flex flex-col items-center justify-center text-center">
            <Upload className="text-slate-100 mb-6" size={64} />
            <h3 className="text-xl font-black uppercase">Load Roster CSV</h3>
            <div className="flex flex-col items-center gap-4 mt-8">
              <label className="cursor-pointer px-12 py-5 bg-slate-900 text-[#FFDB00] rounded font-black uppercase text-[10px] tracking-widest hover:bg-black transition-all shadow-xl">
                Select File
                <input type="file" className="hidden" accept=".csv" onChange={handleFileChange} />
              </label>
              <button onClick={downloadTemplate} className="text-slate-400 hover:text-slate-900 text-[10px] font-black uppercase tracking-widest">
                <Download size={14} className="inline mr-2" /> Download Template
              </button>
            </div>
          </div>
        )}

        {step === 'mapping' && (
          <div className="p-10 space-y-8">
             <div className="grid grid-cols-1 gap-2">
               {Object.keys(INITIAL_MAPPING).map(field => (
                  <div key={field} className="flex items-center justify-between p-4 bg-slate-50 rounded border border-slate-100">
                    <span className="font-black text-[11px] uppercase">{field.replace('_', ' ')}</span>
                    <select value={mapping[field]} onChange={(e) => setMapping(prev => ({ ...prev, [field]: e.target.value }))} className="bg-white border border-slate-200 rounded px-4 py-2 text-[11px] font-bold w-64 shadow-inner">
                      <option value="">-- Unmapped --</option>
                      {Object.keys(parsedData[0] || {}).map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
               ))}
             </div>
             <div className="flex justify-between pt-8 border-t border-slate-50">
               <Button variant="ghost" onClick={() => setStep('upload')}>Back</Button>
               <Button onClick={startReconciliation} disabled={!mapping.first_name || !mapping.email} isLoading={isProcessing}>Analyze Roster</Button>
             </div>
          </div>
        )}

        {step === 'reconciliation' && (
          <div className="p-10 space-y-8">
             <div className="overflow-x-auto border border-slate-200 rounded-md">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-900 text-[#FFDB00] text-[9px] font-black uppercase tracking-widest">
                    <tr><th className="px-6 py-4">Identity</th><th className="px-6 py-4">Organization</th><th className="px-6 py-4 text-right">Status</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {reconciliation.rows.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50/50">
                        <td className="px-6 py-4">
                          <p className="font-black uppercase text-slate-900 text-[11px] truncate">{r.csvRow[mapping.first_name]} {r.csvRow[mapping.last_name]}</p>
                        </td>
                        <td className="px-6 py-4">
                           <SearchableSelect options={allOrgs || []} value={r.manualOrgId || r.matchedOrgId || ''} onChange={(val) => {
                               const next = [...reconciliation.rows];
                               next[i].manualOrgId = val;
                               setReconciliation({ ...reconciliation, rows: next });
                             }} placeholder="Search Orgs..." />
                        </td>
                        <td className="px-6 py-4 text-right">
                           {r.isAlreadyEnrolled ? <div className="text-amber-600 font-black uppercase text-[8px] bg-amber-50 px-2 py-0.5 rounded border border-amber-100 inline-flex items-center gap-1"><ShieldCheck size={10} /> Enrolled</div> : <div className="text-emerald-600 font-black uppercase text-[8px] bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100 inline-flex items-center gap-1"><Users size={10} /> New Enrollment</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
             </div>
             <div className="flex gap-4 pt-6 border-t border-slate-50">
                <Button variant="outline" onClick={() => setStep('mapping')} className="flex-1">Back</Button>
                <Button onClick={runImport} className="flex-[2] py-4 shadow-xl" icon={<Activity size={18} />}>Commit Enrollment</Button>
             </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="flex-1 p-20 text-center flex flex-col items-center justify-center">
            <Loader2 className="animate-spin text-slate-900 mb-6" size={64} />
            <h3 className="text-xl font-black uppercase text-slate-900">Synchronizing Roster</h3>
          </div>
        )}

        {step === 'complete' && (
          <div className="flex-1 p-20 text-center flex flex-col items-center justify-center animate-in zoom-in-95">
            <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mb-8 shadow-inner"><CheckCircle2 size={40} /></div>
            <h3 className="text-3xl font-black text-slate-900 uppercase mb-8">Enrollment Finished</h3>
            <div className="grid grid-cols-3 gap-4 w-full max-w-2xl mb-12">
              <div className="p-8 bg-slate-50 rounded border border-slate-100 text-center"><p className="text-4xl font-black">{importResults.success}</p><p className="text-[8px] font-black text-slate-400 uppercase mt-2">NEW PEOPLE</p></div>
              <div className="p-8 bg-slate-50 rounded border border-slate-100 text-center"><p className="text-4xl font-black">{importResults.updated}</p><p className="text-[8px] font-black text-slate-400 uppercase mt-2">UPDATED</p></div>
              <div className="p-8 bg-blue-50 rounded border border-blue-100 text-center"><p className="text-4xl font-black text-blue-600">{importResults.enrolled}</p><p className="text-[8px] font-black text-blue-400 uppercase mt-2">ENROLLED</p></div>
            </div>
            <Button onClick={() => navigate(-1)} className="px-16 py-5 shadow-2xl">Return to Engagement</Button>
          </div>
        )}
      </div>
    </div>
  );
};